import { getIn, useFormikContext } from 'formik';
import * as React from 'react';

import { Checkbox, Label } from '@/design-system/components/ui';

export interface FormikCheckboxProps {
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const FormikCheckbox = React.forwardRef<HTMLButtonElement, FormikCheckboxProps>(
  ({ name, label, disabled, className }, ref) => {
    const { values, errors, touched, setFieldValue } = useFormikContext();

    const value = getIn(values, name);
    const error = getIn(errors, name);
    const fieldTouched = getIn(touched, name);

    const id = React.useId();
    const errorId = React.useId();
    const hasError = Boolean(fieldTouched && error);
    const errorMessage = fieldTouched && typeof error === 'string' ? error : undefined;

    const handleCheckedChange = React.useCallback(
      (checked: boolean | 'indeterminate') => setFieldValue(name, checked === true),
      [name, setFieldValue],
    );

    return (
      <div className={className}>
        <div className="tw:flex tw:items-center tw:gap-1">
          <Checkbox
            ref={ref}
            id={id}
            checked={Boolean(value)}
            onCheckedChange={handleCheckedChange}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
          />
          {label && <Label htmlFor={id}>{label}</Label>}
        </div>
        {errorMessage && (
          <p id={errorId} className="tw:mt-1 tw:text-sm tw:font-inter tw:text-red-500" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    );
  },
);

FormikCheckbox.displayName = 'FormikCheckbox';

export { FormikCheckbox };
